import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { authApi, sessionsApi } from '@/api/endpoints';
import { WeekCalendar } from '@/components/shared/week-calendar';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/agenda')({ component: AgendaPage });

function AgendaPage() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me });
  const isTherapist = !!me?.therapistProfile;
  const [view, setView] = useState<'calendar' | 'list'>('calendar');

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Minha agenda</h1>
          <p className="text-slate-500 text-sm mt-1">Visão semanal das sessões</p>
        </div>

        <div className="inline-flex p-1 bg-slate-100 rounded-lg text-sm">
          <button
            onClick={() => setView('calendar')}
            className={cn(
              'px-3 py-1.5 rounded-md font-medium transition',
              view === 'calendar' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
            )}
          >
            📅 Calendário
          </button>
          <button
            onClick={() => setView('list')}
            className={cn(
              'px-3 py-1.5 rounded-md font-medium transition',
              view === 'list' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700',
            )}
          >
            ☰ Lista
          </button>
        </div>
      </div>

      {view === 'calendar' ? (
        <WeekCalendar perspective={isTherapist ? 'THERAPIST' : 'CLIENT'} />
      ) : (
        <ListView />
      )}
    </div>
  );
}

function ListView() {
  const { data = [] } = useQuery({ queryKey: ['sessions'], queryFn: () => sessionsApi.list() });
  const upcoming = (data as any[])
    .filter((s) => s.status !== 'CANCELLED')
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {upcoming.length === 0 ? (
        <div className="p-12 text-center text-sm text-slate-400">Sem sessões agendadas.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {upcoming.map((s: any) => (
            <li key={s.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition">
              <div className="text-sm">
                <div className="font-medium text-slate-800">
                  {new Date(s.scheduledAt).toLocaleString('pt-BR', {
                    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </div>
                <div className="text-xs text-slate-400">
                  {s.modality === 'ONLINE' ? 'Online' : 'Presencial'} · {s.durationMin}min
                </div>
              </div>
              <Link to="/dashboard/sessoes/$id" params={{ id: s.id }} className="text-sm text-teal-600 hover:underline font-medium">
                Ver →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
